/**
 * Company: SPHMedia
 * Description: [kaki makan load more restaurants]
 */

import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import useSetFirstRenderedArticleIds from "@components/Modules/LoadMore/SetFirstRenderedArticleIds";
import { KAKI_MAKAN_EVENT_CATEGORY } from "@config/kakiMakan";
import fetchBHApi from "@helper/fetchBHApi";
import { ProcessedArticleData } from "@hooks/transformer/useOSResponse";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import { useCallback, useEffect, useState } from "react";

type useLoadMoreData = {
  loadMoreData: ProcessedArticleData[];
  showLoadMore: boolean;
  handleLoadMore: () => Promise<number>;
  hasMoreStories: boolean;
  pageNumber: number;
};

type useLoadMoreProps = {
  kakiMakanArticles: ProcessedArticleData[];
  initial_restaurant_count: number;
  page_size: number;
  searchQuery: string;
  filterQuery: string | null;
};

/**
 * Custom React Hook: useLoadMoreHook
 * Description: [Hook to load more data for infinite scroll]
 *
 * Key Responsibilities:
 * 1. Keeps track of rendered article IDs to avoid duplicates.
 * 2. Resets state whenever initial props change (e.g., new search/filter results).
 * 3. Fetches the next page of restaurants from the API and appends to current list.
 *
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useLoadMore({
  kakiMakanArticles,
  initial_restaurant_count,
  page_size,
  searchQuery,
  filterQuery,
}: useLoadMoreProps): useLoadMoreData {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const [showLoadMore, setShowLoadMore] = useState<boolean>(false);
  const bhCustomEvent = useBHCustomEventAnalyticsCall();

  /**
   * Sets a precaution for duplicate article ids when rendering additional articles
   */
  useSetFirstRenderedArticleIds(kakiMakanArticles, setRenderedArticleIds);

  const fetchMoreRestaurantsData = useCallback(
    async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[]> => {
      const params = new URLSearchParams();
      params.append(
        "sort",
        Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
      );

      if (searchQuery) params.append("search", searchQuery);
      if (filterQuery) params.append("filter", filterQuery);
      const response = await fetchBHApi<ProcessedArticleData[]>(
        `kaki-makan/restaurants?${params.toString()}`,
        "GET",
      );
      return response;
    },
    [searchQuery, filterQuery],
  );

  const handleLoadMore = useCallback(async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? kakiMakanArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      sort: lastLoadedArticle && lastLoadedArticle.sort,
    };

    const fetchFunction: FetchMoreArticlesFunction = fetchMoreRestaurantsData;
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: page_size,
      pageNumber,
    };
    await CreateLoadMoreHandler(createLoadMoreProps);
    bhCustomEvent(
      KAKI_MAKAN_EVENT_CATEGORY,
      "click",
      "view more",
      undefined,
      CD_LANGUAGE.ENGLISH,
    );

    // Avoid virtual_pv event push
    return 0;
  }, [
    fetchMoreRestaurantsData,
    bhCustomEvent,
    kakiMakanArticles,
    loadMoreData,
    pageNumber,
    page_size,
    renderedArticleIds,
  ]);

  // Reset hook state whenever the incoming restaurant list changes.
  // This happens typically after a new search or filter is applied.
  useEffect(() => {
    setPageNumber(0);
    setLoadMoreData([]);
    setRenderedArticleIds(new Set());
    setShowLoadMore(kakiMakanArticles.length === initial_restaurant_count);
    setHasMoreStories(true);
  }, [kakiMakanArticles, initial_restaurant_count]);

  return {
    loadMoreData,
    showLoadMore,
    handleLoadMore,
    hasMoreStories,
    pageNumber,
  };
}
